"use client";

import React from "react";
import {
  ArrowLeftRight,
  Award,
  BarChart3,
  Bot,
  Brain,
  Briefcase,
  Building2,
  Check,
  CheckCircle2,
  Cloud,
  Coins,
  Database,
  DollarSign,
  GitBranch,
  Globe,
  Laptop,
  Lightbulb,
  ListChecks,
  MessagesSquare,
  Network,
  Presentation,
  RefreshCw,
  Settings,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Star,
  TrendingUp,
  UserRound,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";

// Minimal monochrome brand marks (currentColor) for cloud providers the
// lucide set has no glyphs for. Kept hand-drawn and simple so they sit
// comfortably next to the lucide stroke style inside the icon tile.
function AwsBrandIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      {/* Simplified AWS wordmark: "w" letterform over the smile-arrow swoosh */}
      <path d="M8.5 5.5 10.25 10.5 12 6.2 13.75 10.5 15.5 5.5" />
      <path d="M3 15c2.7 2.3 6 3.5 9.4 3.5 2.9 0 5.7-.8 8-2.3" />
      <path d="M17.8 12.9c1.3.6 2.4 1.5 3.2 2.7l-3.5 1.3" />
    </svg>
  );
}

function MicrosoftBrandIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      {/* Microsoft four-square mark */}
      <path d="M3 3h8.5v8.5H3z" />
      <path d="M12.5 3H21v8.5h-8.5z" />
      <path d="M3 12.5h8.5V21H3z" />
      <path d="M12.5 12.5H21V21h-8.5z" />
    </svg>
  );
}

function GoogleBrandIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 488 512" fill="currentColor" {...props}>
      {/* Google "G" mark */}
      <path d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z" />
    </svg>
  );
}

type IconComponentType = React.ComponentType<React.SVGProps<SVGSVGElement>>;

// Legacy Font Awesome class strings mapped to lucide equivalents (plus the
// inline brand marks above) so the service pages render a single icon system.
const FA_ICON_MAP: Record<string, LucideIcon | IconComponentType> = {
  "fas fa-laptop-code": Laptop,
  "fas fa-mobile-alt": Smartphone,
  "fas fa-globe": Globe,
  "fas fa-sync": RefreshCw,
  "fas fa-certificate": Award,
  "fas fa-chalkboard-teacher": Presentation,
  "fas fa-briefcase": Briefcase,
  "fas fa-brain": Brain,
  "fas fa-sitemap": Network,
  "fas fa-coins": Coins,
  "fas fa-check": Check,
  "fas fa-check-circle": CheckCircle2,
  "fas fa-chart-line": TrendingUp,
  "fas fa-chart-bar": BarChart3,
  "fas fa-dollar-sign": DollarSign,
  "fas fa-lightbulb": Lightbulb,
  "fas fa-code-branch": GitBranch,
  "fas fa-shield-alt": ShieldCheck,
  "fas fa-robot": Bot,
  "fas fa-comments": MessagesSquare,
  "fas fa-user-tie": UserRound,
  "fas fa-cogs": Settings,
  "fas fa-exchange-alt": ArrowLeftRight,
  "fas fa-database": Database,
  "fas fa-cloud": Cloud,
  "fas fa-tasks": ListChecks,
  "fas fa-users": Users,
  "fas fa-star": Star,
  "fas fa-building": Building2,
  "fab fa-aws": AwsBrandIcon,
  "fab fa-microsoft": MicrosoftBrandIcon,
  "fab fa-google": GoogleBrandIcon,
};

export interface ServiceFeatureCardProps {
  icon: string | React.ReactNode;
  title: string;
  description: string;
  features?: string[];
  variant?: "default" | "inline-icon" | "numbered";
  className?: string;
}

export default function ServiceFeatureCard({
  icon,
  title,
  description,
  features,
  variant = "default",
  className = ""
}: ServiceFeatureCardProps) {
  const locale = useLocale();
  const translate = (text: string) => t(locale, text);
  const translatedTitle = translate(title);
  const translatedDescription = description ? translate(description) : "";
  const translatedFeatures = features?.map(translate);

  const renderIcon = () => {
    if (typeof icon === "string") {
      const IconComponent = FA_ICON_MAP[icon] ?? Sparkles;
      return <IconComponent className="h-5 w-5 text-primary" aria-hidden />;
    }
    return icon;
  };

  const iconBadge = (
    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10">
      {renderIcon()}
    </div>
  );

  return (
    <div
      className={`group flex h-full flex-col rounded-2xl border border-border bg-card p-7 transition-colors duration-200 hover:border-neutral-border ${className}`}
    >
      {variant === "inline-icon" ? (
        <div className="mb-4 flex items-center gap-4">
          {iconBadge}
          <h3 className="font-display text-xl font-semibold text-neutral-emphasis">
            {translatedTitle}
          </h3>
        </div>
      ) : (
        <>
          <div className="mb-5">{iconBadge}</div>
          <h4 className="mb-3 font-display text-xl font-semibold text-neutral-emphasis">
            {translatedTitle}
          </h4>
        </>
      )}

      {translatedDescription ? (
        <p className="mb-4 text-sm leading-relaxed text-neutral-muted">
          {translatedDescription}
        </p>
      ) : null}

      {translatedFeatures && translatedFeatures.length > 0 && (
        <ul className="list-disc space-y-2 pl-6 text-sm text-neutral-muted">
          {translatedFeatures.map((feature, index) => (
            <li key={index}>{feature}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
